import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, FileText, X, Plus, Trash2, Sparkles, Cloud, ExternalLink } from "lucide-react";
import { PlatformsPicker, ContractTypeField } from "./EmpPickers";
import { useVehicleTypes } from "./VehicleTypeTools";
import { api } from "../lib/api";
import { DOC_TYPES, allEmployeeStatuses } from "../lib/constants";
import { EmpStatusManager } from "./EmpStatusManager";
import { PLATFORM_STYLES } from "../lib/ridersPlatform";
import { Checkbox } from "./ui/checkbox";
import { useLang } from "../i18n/LanguageContext";

import { EmployeeCustomFieldsInputs, useEmployeeCustomFields } from "./EmployeeCustomFieldsInputs";
const RIDER_PLATFORMS = Object.keys(PLATFORM_STYLES);
const ALL_PLATFORMS = "الكل";

const emptyDocs = () => DOC_TYPES.reduce((a, d) => ({ ...a, [d.key]: { expiry_date: "", attachment_id: null, attachment_name: null } }), {});

const emptyForm = () => ({
  branch: "", name: "", employee_number: "", iqama_number: "", nationality: "",
  profession: "", vehicle_number: "", passport_number: "", fuel_code: "", employee_status: "",
  department: "", platform: ALL_PLATFORMS, platforms: [], contract_type: "", job_title: "", salary: "", rider_id: "", phone: "", vehicle_type: "", fuel_limit: "", fuel_charge_mode: "", birth_date: "", is_supervisor: false, telegram_chat_id: "",
  hire_date: "", app_vehicle_type: "", national_address_short: "", email: "", contract_start: "", contract_end: "", custom_fields: {},
  documents: emptyDocs(), custom_documents: [],
});

const PROFILE_FIELD_KEYS = [
  { key: "name", required: true }, { key: "branch" }, { key: "job_title" },
  { key: "salary", ltr: true }, { key: "employee_number" }, { key: "rider_id", ltr: true },
  { key: "phone", ltr: true, label: "رقم الجوال", ph: "05xxxxxxxx" },
  { key: "iqama_number", ltr: true }, { key: "nationality" }, { key: "profession" },
  { key: "vehicle_number", ltr: true }, { key: "passport_number", ltr: true }, { key: "fuel_code", ltr: true },
  { key: "birth_date", ltr: true, label: "تاريخ الميلاد", ph: "YYYY-MM-DD" },
  { key: "app_vehicle_type", label: "نوع المركبة المسجل في التطبيق", ph: "مثال: سيارة / دباب" },
  { key: "national_address_short", ltr: true, label: "العنوان الوطني المختصر", ph: "ABCD1234" },
  { key: "email", ltr: true, label: "البريد الإلكتروني", ph: "name@example.com", type: "email" },
  { key: "contract_start", ltr: true, label: "تاريخ بداية العقد", type: "date" },
  { key: "contract_end", ltr: true, label: "تاريخ نهاية العقد", type: "date" },
];

const PROFILE_AI_KEYS = ["name", "iqama_number", "passport_number", "nationality", "profession", "employee_number"];

export const EmployeeDialog = ({ open, onOpenChange, onSaved, initial, existingEmployees = [], branchOptions = [], onOpenEmployee }) => {
  const { t, dir } = useLang();
  const { all: vTypes } = useVehicleTypes();
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [statusMgr, setStatusMgr] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [platforms, setPlatforms] = useState(RIDER_PLATFORMS);
  const isEdit = Boolean(initial);
  const customFields = useEmployeeCustomFields(open);

  useEffect(() => {
    if (!open) return;
    api.ridersConfig()
      .then((d) => { if (Array.isArray(d?.platforms) && d.platforms.length) setPlatforms(d.platforms); })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      if (initial) {
        const docs = emptyDocs();
        DOC_TYPES.forEach((d) => {
          const src = initial.documents?.[d.key] || {};
          docs[d.key] = {
            expiry_date: src.expiry_date || "",
            attachment_id: src.attachment_id || null,
            attachment_name: src.attachment_name || null,
            ...(d.key === "operation_card" ? { card_number: src.card_number || "" } : {}),
          };
        });
        setForm({
          branch: initial.branch || "", name: initial.name || "", employee_number: initial.employee_number || "",
          iqama_number: initial.iqama_number || "", nationality: initial.nationality || "",
          profession: initial.profession || "", vehicle_number: initial.vehicle_number || "",
          passport_number: initial.passport_number || "", fuel_code: initial.fuel_code || "", employee_status: initial.employee_status || initial.tashark_status || "",
          department: initial.department || "", job_title: initial.job_title || "", salary: initial.salary || "", rider_id: initial.rider_id || "", phone: initial.phone || "", vehicle_type: initial.vehicle_type || "", fuel_limit: initial.fuel_limit ?? "", fuel_charge_mode: initial.fuel_charge_mode || "", birth_date: initial.birth_date || "", is_supervisor: !!initial.is_supervisor, telegram_chat_id: initial.telegram_chat_id || "",
          hire_date: initial.hire_date || "", app_vehicle_type: initial.app_vehicle_type || "", national_address_short: initial.national_address_short || "",
          email: initial.email || "", contract_start: initial.contract_start || "", contract_end: initial.contract_end || "", custom_fields: initial.custom_fields || {},
          platforms: initial.platforms?.length ? initial.platforms : (initial.platform && initial.platform !== ALL_PLATFORMS ? [initial.platform] : []),
          platform: initial.platform || ALL_PLATFORMS, contract_type: initial.contract_type || "",
          documents: docs,
          custom_documents: (initial.custom_documents || []).map((c) => ({
            id: c.id, label: c.label || "", expiry_date: c.expiry_date || "",
            attachment_id: c.attachment_id || null, attachment_name: c.attachment_name || null,
          })),
        });
      } else {
        setForm(emptyForm());
      }
    }
  }, [open, initial]);

  const setDoc = (key, patch) =>
    setForm((f) => ({ ...f, documents: { ...f.documents, [key]: { ...f.documents[key], ...patch } } }));

  const addCustomDoc = () =>
    setForm((f) => ({ ...f, custom_documents: [...f.custom_documents, { label: "", expiry_date: "", attachment_id: null, attachment_name: null }] }));
  const [reqDocs, setReqDocs] = useState([]);
  useEffect(() => { if (open) api.requiredDocs().then((r) => setReqDocs(r.items || [])).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!open || !reqDocs.length) return;
    setForm((f) => {
      const ct = (f.contract_type || "").trim();
      const have = new Set(f.custom_documents.map((c) => (c.label || "").trim()));
      const std = new Set(DOC_TYPES.map((d) => d.label));
      const need = reqDocs.filter((rd) => (!rd.contract_types?.length || rd.contract_types.includes(ct)) && !have.has(rd.label) && !std.has(rd.label));
      const keep = f.custom_documents.filter((c) => !(c.required && !c.attachment_id && !c.expiry_date && !need.some((n) => n.label === c.label)
        && !reqDocs.some((rd) => rd.label === c.label && (!rd.contract_types?.length || rd.contract_types.includes(ct)))));
      if (!need.length && keep.length === f.custom_documents.length) return f;
      return { ...f, custom_documents: [...keep.map((c) => (reqDocs.some((rd) => rd.label === (c.label || "").trim()) ? { ...c, required: true } : c)),
        ...need.map((rd) => ({ label: rd.label, expiry_date: "", attachment_id: null, attachment_name: null, required: true }))] };
    });
  }, [open, reqDocs, form.contract_type]);
  const setCustom = (i, patch) =>
    setForm((f) => ({ ...f, custom_documents: f.custom_documents.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeCustom = (i) =>
    setForm((f) => ({ ...f, custom_documents: f.custom_documents.filter((_, idx) => idx !== i) }));

  const applyProfileToForm = (next, res) => {
    const changed = [];
    PROFILE_AI_KEYS.forEach((k) => {
      const val = res[k] ? String(res[k]).trim() : "";
      if (val && val !== String(next[k] || "").trim()) {
        next[k] = val;
        changed.push(k);
      }
    });
    return changed;
  };

  const extractToast = (res, changedKeys) => {
    const parts = [];
    if (res.expiry_date) parts.push(t("dash.modals.emp.toast.expiryPart", { d: res.expiry_date }));
    if (res.plate_number) parts.push(`اللوحة: ${res.plate_number}${res.plate_number_en ? ` / ${res.plate_number_en}` : ""}`);
    (changedKeys || []).forEach((k) => parts.push(`${k === "birth_date" ? "تاريخ الميلاد" : t(`dash.modals.emp.aiLabels.${k}`)}: ${res[k]}`));
    if (parts.length) toast.success(t("dash.modals.emp.toast.readUpdated", { parts: parts.join(" • ") }));
    else toast.success(t("dash.modals.emp.toast.uploadedNoData"));
  };

  // OCR profile fields (name, iqama_number, nationality...) must ONLY be applied when reading an
  // identity document. Reading any other file (كرت التشغيل، الاستمارة، التأمين...) must update ONLY
  // that document's own data (expiry + attachment), never the employee name or other profile fields.
  const ID_DOC_KEYS = new Set(["passport", "iqama"]);
  const applyExtractResult = (res, target) => {
    let changedKeys = [];
    const allowProfile = target.type === "doc" && ID_DOC_KEYS.has(target.key);
    setForm((f) => {
      const next = { ...f };
      if (allowProfile) {
        changedKeys = applyProfileToForm(next, res);
        if (res.birth_date && res.birth_date !== next.birth_date) { next.birth_date = res.birth_date; changedKeys = [...changedKeys, "birth_date"]; }
      }
      const patch = { attachment_id: res.attachment_id, attachment_name: res.attachment_name };
      if (res.expiry_date) patch.expiry_date = res.expiry_date;
      if (res.card_number) patch.card_number = res.card_number;
      if (res.plate_number) { patch.plate_number = res.plate_number; patch.plate_number_en = res.plate_number_en || ""; if (!next.vehicle_number) next.vehicle_number = res.plate_number; }
      if (target.type === "doc") {
        next.documents = { ...f.documents, [target.key]: { ...f.documents[target.key], ...patch } };
        if (target.key === "contract") {
          if (res.start_date) next.contract_start = res.start_date;
          if (res.expiry_date) next.contract_end = res.expiry_date;
        }
      } else {
        next.custom_documents = f.custom_documents.map((c, idx) => (idx === target.index ? { ...c, ...patch } : c));
      }
      return next;
    });
    extractToast(res, changedKeys);
  };

  // Istimara with an authorized driver always goes to that rider's main istimara slot.
  const routeIstimara = async (res, target) => {
    const ae = res.authorized_employee;
    if (!ae) return target;
    const RIDER = "rider_vehicle_registration";
    if (ae.id === initial?.id || String(form.iqama_number || "").replace(/\D/g, "") === res.authorized_driver_id) {
      if (target.key !== RIDER) toast.info("الاستمارة مفوّض عليها هذا المندوب — وُضعت في خانة «استمارة مركبة المندوب الأساسية»");
      return { type: "doc", key: RIDER };
    }
    await api.docFolderAttach(ae.id, RIDER, { attachment_id: res.attachment_id, attachment_name: res.attachment_name, expiry_date: res.expiry_date || null });
    toast.warning(`هذه الاستمارة مفوّض عليها ${ae.name} — حُفظت في ملفه (استمارة مركبة المندوب الأساسية) وليس هنا`, { duration: 8000 });
    return null;
  };

  const handleCustomUpload = async (i, file) => {
    if (!file) return;
    setUploading(`c${i}`);
    try {
      const cd = form.custom_documents[i];
      const res = await api.uploadExtract(file, cd?.label || "");
      const tg = await routeIstimara(res, { type: "custom", index: i });
      if (tg) applyExtractResult(res, tg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.modals.emp.toast.uploadFail"));
    } finally {
      setUploading(null);
    }
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const label = DOC_TYPES.find((d) => d.key === key)?.label || "";
      const res = await api.uploadExtract(file, label);
      const tg = await routeIstimara(res, { type: "doc", key });
      if (tg) applyExtractResult(res, tg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.modals.emp.toast.uploadFail"));
    } finally {
      setUploading(null);
    }
  };

  const [drive, setDrive] = useState(null); // { target, label }
  const [driveUrl, setDriveUrl] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);

  const submitDrive = async () => {
    if (!driveUrl.trim()) { toast.error(t("dash.modals.emp.toast.driveEmpty")); return; }
    setDriveBusy(true);
    try {
      const res = await api.importDrive(driveUrl.trim(), drive.label || "");
      const tg = await routeIstimara(res, drive.target);
      if (tg) applyExtractResult(res, tg);
      setDrive(null);
      setDriveUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.modals.emp.toast.driveFail"));
    } finally {
      setDriveBusy(false);
    }
  };

  const [dupWarn, setDupWarn] = useState(false);

  const duplicateName = (() => {
    const q = (form.iqama_number || "").trim();
    if (!q) return null;
    const other = (existingEmployees || []).find(
      (e) => (e.iqama_number || "").trim() === q && e.id !== initial?.id
    );
    return other ? other.name : null;
  })();

  const buildPayload = () => ({
    ...form,
    salary: Number(form.salary) || 0,
    hire_date: form.hire_date || null, contract_start: form.contract_start || null, contract_end: form.contract_end || null,
    fuel_limit: form.fuel_limit === "" || form.fuel_limit === null ? null : Number(form.fuel_limit),
    documents: DOC_TYPES.reduce((a, d) => {
      const doc = form.documents[d.key];
      a[d.key] = {
        expiry_date: doc.expiry_date || null,
        attachment_id: doc.attachment_id || null,
        attachment_name: doc.attachment_name || null,
        ...(d.key === "operation_card" ? { card_number: (doc.card_number || "").trim() } : {}),
      };
      return a;
    }, {}),
    custom_documents: form.custom_documents.filter((c) => c.label.trim() && !(c.required && !c.attachment_id && !c.expiry_date)).map((c) => ({
      id: c.id || undefined,
      label: c.label.trim(),
      expiry_date: c.expiry_date || null,
      attachment_id: c.attachment_id || null,
      attachment_name: c.attachment_name || null,
    })),
  });

  const doSave = async () => {
    setDupWarn(false);
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = isEdit ? await api.updateEmployee(initial.id, payload) : await api.createEmployee(payload);
      const sh = saved?.opcard_shared;
      if (sh?.count > 1) {
        toast.warning(`تنبيه: الكرت ${sh.number} مكرر بعدد ${sh.count} مندوب${sh.others?.length ? ` (${sh.others.join("، ")})` : ""}`, { duration: 8000 });
      } else if (sh) {
        toast.info(`تم تحديث الكرت ${sh.number} في كروت التشغيل والمركبات`);
      }
      toast.success(isEdit ? t("dash.modals.emp.toast.updated") : t("dash.modals.emp.toast.added"));
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.modals.emp.toast.saveErr"));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) { toast.error(t("dash.modals.emp.toast.nameReq")); return; }
    if (duplicateName) { setDupWarn(true); return; }
    doSave();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{isEdit ? t("dash.modals.emp.editTitle") : t("dash.modals.emp.addTitle")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.modals.emp.desc")}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile" dir={dir} className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="profile" data-testid="tab-profile" className="flex-1">{t("dash.modals.emp.tabProfile")}</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="flex-1">{t("dash.modals.emp.tabDocuments")}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PROFILE_FIELD_KEYS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={`emp-${f.key}`}>{f.label || t(`dash.modals.emp.fields.${f.key}.l`)}{f.required ? " *" : ""}</Label>
                  <Input
                    id={`emp-${f.key}`} data-testid={`emp-${f.key}-input`}
                    dir={f.ltr ? "ltr" : dir} placeholder={f.ph || (f.label ? "" : t(`dash.modals.emp.fields.${f.key}.p`))} type={f.type || "text"}
                    list={f.key === "branch" ? "branch-options-list" : undefined}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
              <EmployeeCustomFieldsInputs fields={customFields} values={form.custom_fields}
                onChange={(k, v) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [k]: v } }))} />
              <datalist id="branch-options-list">
                {branchOptions.map((b) => <option key={b} value={b} />)}
              </datalist>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("dash.modals.emp.status")}</Label>
                  <button type="button" onClick={() => setStatusMgr(true)} className="text-[11px] font-semibold text-blue-600 hover:underline" data-testid="emp-status-manage">+ إدارة الحالات</button>
                </div>
                <Select value={form.employee_status} onValueChange={(v) => setForm({ ...form, employee_status: v })}>
                  <SelectTrigger data-testid="emp-employee-status-select"><SelectValue placeholder={t("dash.modals.emp.statusPh")} /></SelectTrigger>
                  <SelectContent>
                    {[...new Set([...allEmployeeStatuses(), form.employee_status].filter(Boolean))].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <EmpStatusManager open={statusMgr} onOpenChange={setStatusMgr} onSaved={() => setForm((f) => ({ ...f }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("dash.modals.emp.vehicleType")}</Label>
                <Select value={form.vehicle_type || ""} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger data-testid="emp-vehicle-type-select"><SelectValue placeholder={t("dash.modals.emp.vehicleTypePh")} /></SelectTrigger>
                  <SelectContent>
                    {Array.from(new Set([...vTypes, ...(form.vehicle_type ? [form.vehicle_type] : [])])).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>حدّ الوقود الشهري (⃁)</Label>
                <Input type="number" min="0" dir="ltr" data-testid="emp-fuel-limit-input" value={form.fuel_limit ?? ""}
                  placeholder="اتركه فارغاً لاستخدام الحدّ العام" onChange={(e) => setForm({ ...form, fuel_limit: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>تحميل البنزين على المندوب</Label>
                <Select value={form.fuel_charge_mode || "default"} onValueChange={(v) => setForm({ ...form, fuel_charge_mode: v === "default" ? "" : v })}>
                  <SelectTrigger data-testid="emp-fuel-charge-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">حسب إعداد الشركة</SelectItem>
                    <SelectItem value="over">فوق الحد فقط</SelectItem>
                    <SelectItem value="full">كامل المبلغ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>تطبيق المندوب</Label>
                <PlatformsPicker value={form.platforms || []} options={platforms} onOptions={setPlatforms}
                  onChange={(v) => setForm({ ...form, platforms: v, platform: v.length === 1 ? v[0] : ALL_PLATFORMS })} />
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>نوع العقد</Label>
              <ContractTypeField value={form.contract_type} onChange={(v) => setForm({ ...form, contract_type: v })} />
            </div>
            <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="emp-supervisor-toggle">
              <Checkbox checked={!!form.is_supervisor} onCheckedChange={(v) => setForm({ ...form, is_supervisor: !!v })} />
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-800">صلاحية مشرف</div>
                <div className="text-[11px] text-slate-500">يظهر له قسم «المهام» في تطبيقه ويمكن إسناد مهام له. لا يُفعَّل تلقائياً — فعّله يدوياً من هنا.</div>
              </div>
            </label>
            </div>
            {form.is_supervisor && (
              <div className="space-y-1.5 mt-2">
                <Label>Chat ID تيليجرام (اختياري)</Label>
                <Input data-testid="emp-telegram-chatid" dir="ltr" value={form.telegram_chat_id}
                  onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
                  placeholder="مثال: 123456789" />
                <p className="text-[11px] text-slate-400">يُرسَل له إشعار خاص عبر بوت المؤسسة عند إسناد مهمة. يحصل عليه بفتح بوت الشركة في تيليجرام وإرسال <code>/myid</code> فيرد عليه البوت برقمه مباشرةً.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="pt-4">
            <div className="space-y-3">
              <div data-testid="ai-extract-hint" className="flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2.5 text-xs text-blue-800">
                <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" />
                <span>{t("dash.modals.emp.aiHint")}</span>
              </div>
              {DOC_TYPES.map((d) => {
                const doc = form.documents[d.key];
                return (
                  <div key={d.key} className="rounded-xl border border-slate-200 p-3.5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-heading font-semibold text-slate-800 min-w-[110px]">{d.label}</span>
                      <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs text-slate-500 whitespace-nowrap">{t("dash.modals.emp.expiry")}</Label>
                          <Input
                            type="date" data-testid={`doc-${d.key}-expiry-input`}
                            className="w-40" value={doc.expiry_date || ""}
                            onChange={(e) => setDoc(d.key, { expiry_date: e.target.value })}
                          />
                        </div>
                        {doc.attachment_id ? (
                          <div className="flex items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-2 py-1 text-xs text-blue-700 max-w-[160px]">
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{doc.attachment_name}</span>
                            <button type="button" data-testid={`doc-${d.key}-remove-attachment`}
                              onClick={() => setDoc(d.key, { attachment_id: null, attachment_name: null })}
                              className="text-blue-500 hover:text-red-500">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                              data-testid={`doc-${d.key}-upload-label`}>
                              {uploading === d.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-blue-600" />}
                              {uploading === d.key ? t("dash.modals.emp.reading") : t("dash.modals.emp.attachRead")}
                              <input type="file" accept="image/*,application/pdf" className="hidden"
                                data-testid={`doc-${d.key}-file-input`}
                                onChange={(e) => handleUpload(d.key, e.target.files?.[0])} />
                            </label>
                            <button type="button" data-testid={`doc-${d.key}-drive-button`}
                              onClick={() => { setDrive({ target: { type: "doc", key: d.key }, label: d.label }); setDriveUrl(""); }}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition">
                              <Cloud className="h-3.5 w-3.5 text-sky-600" /> {t("dash.modals.emp.drive")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {d.key === "operation_card" && (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid="opcard-number-block">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label className="text-xs text-slate-500 whitespace-nowrap">رقم كرت التشغيل</Label>
                          <Input data-testid="opcard-number-input" dir="ltr" className="w-48 font-mono-nums"
                            placeholder="يُقرأ تلقائياً من الملف"
                            value={doc.card_number || ""}
                            onChange={(e) => setDoc("operation_card", { card_number: e.target.value })} />
                        </div>
                        {(() => {
                          const num = (doc.card_number || "").trim();
                          const sharers = num ? existingEmployees.filter((e) => e.id !== initial?.id && ((e.documents?.operation_card?.card_number || "").trim() === num)) : [];
                          if (!num || sharers.length === 0) return null;
                          return (
                            <details className="text-xs" data-testid="opcard-shared-details">
                              <summary className="cursor-pointer text-amber-700 font-semibold">
                                تنبيه: هذا الكرت مكرر بعدد {sharers.length + 1} مندوب — عرض الأسماء
                              </summary>
                              <ul className="mt-1.5 space-y-1 pr-2">
                                {sharers.map((e) => (
                                  <li key={e.id} data-testid="opcard-shared-name" className="text-slate-600 flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {e.name}{e.branch ? ` — ${e.branch}` : ""}
                                    {onOpenEmployee && (
                                      <button type="button" data-testid={`opcard-open-sharer-${e.id}`}
                                        onClick={() => onOpenEmployee(e)}
                                        className="mr-auto inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100">
                                        <ExternalLink className="h-3 w-3" /> فتح البطاقة
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="rounded-xl border border-dashed border-slate-300 p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-semibold text-slate-800">{t("dash.modals.emp.extraDocs")}</span>
                  <Button type="button" size="sm" variant="outline" data-testid="add-custom-doc-button"
                    className="gap-1.5 h-8" onClick={addCustomDoc}>
                    <Plus className="h-3.5 w-3.5" /> {t("dash.modals.emp.addDoc")}
                  </Button>
                </div>
                {form.custom_documents.length === 0 && (
                  <p className="text-xs text-slate-400">{t("dash.modals.emp.extraHint")}</p>
                )}
                <div className="space-y-2">
                  {form.custom_documents.map((cd, i) => (
                    <div key={i} data-testid={`custom-doc-${i}`} className={`flex items-center gap-2 flex-wrap rounded-lg p-2 ${cd.required && !cd.attachment_id ? "bg-orange-50 ring-1 ring-orange-200" : "bg-slate-50"}`}>
                      {cd.required && <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`custom-doc-${i}-required`}>إلزامي</span>}
                      <Input data-testid={`custom-doc-${i}-label`} placeholder={t("dash.modals.emp.docNamePh")} className="w-36"
                        value={cd.label} onChange={(e) => setCustom(i, { label: e.target.value })} />
                      <Input type="date" data-testid={`custom-doc-${i}-expiry`} className="w-40"
                        value={cd.expiry_date || ""} onChange={(e) => setCustom(i, { expiry_date: e.target.value })} />
                      {cd.attachment_id ? (
                        <div className="flex items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-2 py-1 text-xs text-blue-700 max-w-[130px]">
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{cd.attachment_name}</span>
                          <button type="button" onClick={() => setCustom(i, { attachment_id: null, attachment_name: null })} className="text-blue-500 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white transition" data-testid={`custom-doc-${i}-upload-label`}>
                            {uploading === `c${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-blue-600" />}
                            {uploading === `c${i}` ? t("dash.modals.emp.readingShort") : t("dash.modals.emp.attachRead")}
                            <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`custom-doc-${i}-file-input`} onChange={(e) => handleCustomUpload(i, e.target.files?.[0])} />
                          </label>
                          <button type="button" data-testid={`custom-doc-${i}-drive-button`}
                            onClick={() => { setDrive({ target: { type: "custom", index: i }, label: cd.label || "" }); setDriveUrl(""); }}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-medium text-slate-600 hover:bg-white transition">
                            <Cloud className="h-3.5 w-3.5 text-sky-600" /> {t("dash.modals.emp.drive")}
                          </button>
                        </div>
                      )}
                      {!cd.required && <button type="button" data-testid={`custom-doc-${i}-remove`} onClick={() => removeCustom(i)} className="text-slate-400 hover:text-red-600 ms-auto"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-200">
          <Button data-testid="save-employee-button" disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition"
            onClick={handleSave}>
            {saving ? t("dash.modals.emp.saving") : isEdit ? t("dash.modals.emp.saveEdits") : t("dash.modals.emp.addEmp")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.modals.emp.cancel")}</Button>
        </DialogFooter>

        <Dialog open={Boolean(drive)} onOpenChange={(v) => !v && setDrive(null)}>
          <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="drive-import-dialog">
            <DialogHeader className="text-start">
              <DialogTitle className="font-heading text-lg flex items-center gap-2">
                <Cloud className="h-5 w-5 text-sky-600" /> {t("dash.modals.emp.driveTitle")}
              </DialogTitle>
              <DialogDescription className="text-start">
                {t("dash.modals.emp.driveDesc")}
                {drive?.label ? t("dash.modals.emp.driveDocSuffix", { label: drive.label }) : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Input data-testid="drive-url-input" dir="ltr" placeholder="https://drive.google.com/file/d/..."
                value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} />
            </div>
            <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
              <Button data-testid="drive-import-submit" disabled={driveBusy}
                className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={submitDrive}>
                {driveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
                {driveBusy ? t("dash.modals.emp.driveImporting") : t("dash.modals.emp.driveImportRead")}
              </Button>
              <Button variant="outline" onClick={() => setDrive(null)}>{t("dash.modals.emp.cancel")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={dupWarn} onOpenChange={setDupWarn}>
          <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="duplicate-warning-dialog">
            <DialogHeader className="text-start">
              <DialogTitle className="font-heading text-lg flex items-center gap-2 text-amber-600">
                <Trash2 className="h-5 w-5" /> {t("dash.modals.emp.dupTitle")}
              </DialogTitle>
              <DialogDescription className="text-start">
                {t("dash.modals.emp.dupDesc", { iqama: form.iqama_number, name: duplicateName })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
              <Button data-testid="duplicate-warning-continue" className="bg-amber-600 hover:bg-amber-700" onClick={doSave}>
                {t("dash.modals.emp.dupContinue")}
              </Button>
              <Button variant="outline" data-testid="duplicate-warning-cancel" onClick={() => setDupWarn(false)}>
                {t("dash.modals.emp.dupReview")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
